import {
    SET_TRACKED_CAMERA_OPTIONS,
    SET_MAP_LOADED,
    SET_MAP_POSITION,
    SET_DRAW_MODULE,
    SET_MAP_GLOBAL_PADDING,
    SET_MAP_FIT_BOUNDS,
    SET_MAP_PROPERTIES,
    SET_DRAW_FEATURES,
    SET_DRAWN_FEATURES,
    SET_SAVED_DRAWN_FEATURES,
    SET_DRAWING_FEATURE,
    SET_REFRESH_MAP,
    SET_DRAWN_FEATURE_COORDINATES,
    SET_DRAWING_MODULE_MODE,
    SET_DRAWING_MODULE_IDLE,
    SET_SHAPE_FEATURE_PROPERTY,
    SAVE_DRAWN_FEATURE_REQUEST,
    SAVE_DRAWN_FEATURE_SUCCESS,
    SAVE_DRAWN_FEATURE_FAILURE,
    FETCH_DRAWN_FEATURES_REQUEST,
    FETCH_DRAWN_FEATURES_SUCCESS,
    FETCH_DRAWN_FEATURES_FAILURE,
    UPDATE_DRAWN_FEATURE_REQUEST,
    UPDATE_DRAWN_FEATURE_SUCCESS,
    UPDATE_DRAWN_FEATURE_FAILURE,
    DELETE_DRAWN_FEATURE_REQUEST,
    DELETE_DRAWN_FEATURE_SUCCESS,
    DELETE_DRAWN_FEATURE_FAILURE,
    CLEAR_SAVED_DRAWN_FEATURES
} from './Types';
import hat from 'hat'; // Included with mapbox-gl-draw
import { MAIN_MAP } from './Constants';
import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { CameraOptions, GlobalPadding, MapProperties, SavedDrawnFeatures } from './mapTypes';
import _ from 'lodash';
import { Feature } from '../../utility/Types/commonTypes';
import { openSnackbar } from '../ui/Actions';
import { Dispatch } from 'redux';

// Trackes camera options as the map changes (lng, lat, zoom, pitch, bearing)
export function setCameraOptions(cameraOptions: CameraOptions, name = '') {
    return { type: SET_TRACKED_CAMERA_OPTIONS, name, cameraOptions };
}

export function setMapLoaded(isLoaded: boolean, name = '') {
    return { type: SET_MAP_LOADED, name, isLoaded };
}

export function setMapPosition(mapPosition: number[][], name = '') {
    return { type: SET_MAP_POSITION, name, mapPosition };
}

export function setGlobalPadding(padding: GlobalPadding, name = '') {
    return { type: SET_MAP_GLOBAL_PADDING, name, padding };
}

export function setFitBounds(bounds: number[], options = {}, name = '') {
    return { type: SET_MAP_FIT_BOUNDS, name, bounds, options };
}

export function setFitBoundsFromGeojson(
    bounds: { coordinates: number[][] },
    options = {},
    name = ''
) {
    const coords = bounds.coordinates;
    return setFitBounds([coords[0][2], coords[0][0]], options, name);
}

export function setMapProperties(properties: MapProperties, name = '') {
    return { type: SET_MAP_PROPERTIES, name, properties };
}

export function setDrawModule(drawModule: any, name = '') {
    return { type: SET_DRAW_MODULE, name, drawModule };
}

// Feature can be feature object or the geometry
export const addDrawFeature = (feature: any, name = ''): any => {
    return (
        dispatch: (arg0: {
            type: string;
            name: string;
            drawnFeatures: { type: string; features: Feature[] };
            selectedFeature: null;
        }) => void,
        getState: () => { (): any; new (): any; [x: string]: any }
    ) => {
        const state = getState()[name];
        if (state.drawModule) {
            const drawControl = state.drawModule.getDrawControl();
            const ids = drawControl.add(feature);
            drawControl.changeMode(
                (feature.type === 'Feature' ? feature.geometry.type === 'Point' : feature.type === 'Point')  ? 'simple_select' : 'direct_select',
                { featureId: ids[0] }
            );
            dispatch(
                setDrawFeatures(
                    name,
                    _.cloneDeep(drawControl.getAll()),
                    _.cloneDeep(drawControl.get(ids[0]))
                )
            );
        } else {
            const newFeature =
                feature.type === 'Feature'
                    ? feature
                    : {
                          geometry: feature,
                          id: hat(),
                          properties: {},
                          type: 'Feature'
                      };
            dispatch(
                setDrawFeatures(
                    name,
                    {
                        type: 'FeatureCollection',
                        features: [...state.drawnFeatures.features, newFeature]
                    },
                    newFeature
                )
            );
        }
    };
};

export const deleteDrawFeature = (ids: any, name = '') => {
    return (
        dispatch: (arg0: {
            type: string;
            name: string;
            drawnFeatures: { type: string; features: Feature[] };
            selectedFeature: null;
        }) => void,
        getState: () => { (): any; new (): any; [x: string]: any }
    ) => {
        const state = getState()[name];
        if (state.drawModule) {
            const drawControl = state.drawModule.getDrawControl();
            drawControl.delete(ids);
            dispatch(setDrawFeatures(name, _.cloneDeep(drawControl.getAll()), null));
        } else {
            // May never be necessary but probably good as an a catch
            console.error('Needs to be implemented');
        }
    };
};

export const changeDrawMode = (name: string | number, mode: any, options = undefined) => {
    return (dispatch: any, getState: () => { (): any; new (): any; [x: string]: any }) => {
        const state = getState()[name];
        if (state.drawModule) {
            const drawControl = state.drawModule.getDrawControl();
            drawControl.changeMode(mode, options);
        } else {
            // May never be necessary but probably good as an a catch
            console.error('Needs to be implemented');
        }
    };
};

export const setDrawnFeatureCoordinates = (id: any, coordinates: any, name = '') => ({
    type: SET_DRAWN_FEATURE_COORDINATES,
    id,
    coordinates,
    name
});

export function setDrawFeatures(
    name = '',
    drawnFeatures: { type: string; features: Feature[] } = {
        type: 'FeatureCollection',
        features: []
    },
    selectedFeature = null
) {
    return { type: SET_DRAW_FEATURES, name, drawnFeatures, selectedFeature };
}

export function setDrawnFeatures(drawnFeatures = {}, name = '') {
    return { type: SET_DRAWN_FEATURES, name, drawnFeatures };
}

export function setSavedDrawnFeatures(savedDrawnFeatures = [], name = '') {
    return { type: SET_SAVED_DRAWN_FEATURES, name, savedDrawnFeatures };
}

export function setClearSavedDrawnFeatures(value: any, name = '') {
    return { type: CLEAR_SAVED_DRAWN_FEATURES, name, value };
}

export function setDrawingFeature(drawingFeature = {}, name = '') {
    return { type: SET_DRAWING_FEATURE, name, drawingFeature };
}

export const setDrawingModuleMode = (mode = null, name = '') => ({
    type: SET_DRAWING_MODULE_MODE,
    mode,
    name
});

export const setDrawingModuleIdle = (name = '') => ({
    type: SET_DRAWING_MODULE_IDLE,
    name
});

export function setRefreshMap(value: any, name = '') {
    return { type: SET_REFRESH_MAP, value: value, name };
}

export const refreshDrawControl = (name = '') => {
    return (dispatch: any, getState: () => { (): any; new (): any; [x: string]: any }) => {
        const mapReducer = getState()[name];
        const drawControl = mapReducer.drawModule.getDrawControl();
        drawControl.set(mapReducer.drawnFeatures);
        if (mapReducer.drawingFeature) {
            drawControl.changeMode('direct_select', {
                featureId: mapReducer.drawingFeature.id
            });
        }
    };
};

export const setShapeFeaturePropertyFunction = (
    name = '',
    drawnFeatures: { type: string; features: Feature[] } = {
        type: 'FeatureCollection',
        features: []
    },
    selectedFeature = null
): any => ({ type: SET_SHAPE_FEATURE_PROPERTY, drawnFeatures, selectedFeature, name });

export const setShapeFeatureProperty = (
    featureId: string,
    userProperty: string,
    userPropertyValue: string,
    name = ''
): any => {
    return (dispatch: any, getState: () => { (): any; new (): any; [x: string]: any }) => {
        const state = getState()[name];
        const drawControl = state.drawModule.getDrawControl();
        drawControl.setFeatureProperty(featureId, userProperty, userPropertyValue);
        const result = drawControl.get(featureId);
        drawControl.add(result);
        dispatch(
            setShapeFeaturePropertyFunction(
                name,
                _.cloneDeep(drawControl.getAll()),
                _.cloneDeep(drawControl.get(featureId))
            )
        );
    };
};

const saveDrawnFeature = (name = MAIN_MAP): { type: string; name: string } => ({
    type: SAVE_DRAWN_FEATURE_REQUEST,
    name
});
const saveDrawnFeatureSuccess = (
    response: any,
    name = MAIN_MAP
): { type: string; response: any; name: string } => ({
    type: SAVE_DRAWN_FEATURE_SUCCESS,
    response,
    name
});
const saveDrawnFeatureFailure = (
    error: any,
    name = MAIN_MAP
): { type: string; error: any; name: string } => ({
    type: SAVE_DRAWN_FEATURE_FAILURE,
    error,
    name
});
const fetchDrawnFeature = (name = MAIN_MAP): { type: string; name: string } => ({
    type: FETCH_DRAWN_FEATURES_REQUEST,
    name
});
const fetchDrawnFeatureSuccess = (
    response: any,
    name = MAIN_MAP
): { type: string; response: any; name: string } => ({
    type: FETCH_DRAWN_FEATURES_SUCCESS,
    response,
    name
});
const fetchDrawnFeatureFailure = (
    error: any,
    name = MAIN_MAP
): { type: string; error: any; name: string } => ({
    type: FETCH_DRAWN_FEATURES_FAILURE,
    error,
    name
});
const updateDrawnFeature = (name = MAIN_MAP): { type: string; name: string } => ({
    type: UPDATE_DRAWN_FEATURE_REQUEST,
    name
});
const updateDrawnFeatureSuccess = (
    response: any,
    name = MAIN_MAP
): { type: string; response: any; name: string } => ({
    type: UPDATE_DRAWN_FEATURE_SUCCESS,
    response,
    name
});
const updateDrawnFeatureFailure = (
    error: any,
    name = MAIN_MAP
): { type: string; error: any; name: string } => ({
    type: UPDATE_DRAWN_FEATURE_FAILURE,
    error,
    name
});
const deleteDrawnFeatureFromDB = (name = MAIN_MAP): { type: string; name: string } => ({
    type: DELETE_DRAWN_FEATURE_REQUEST,
    name
});
const deleteDrawnFeatureSuccess = (
    response: any,
    name = MAIN_MAP
): { type: string; response: any; name: string } => ({
    type: DELETE_DRAWN_FEATURE_SUCCESS,
    response,
    name
});
const deleteDrawnFeatureFailure = (
    error: any,
    name = MAIN_MAP
): { type: string; error: any; name: string } => ({
    type: DELETE_DRAWN_FEATURE_FAILURE,
    error,
    name
});

interface PayloadStructure {
    name: string;
    stroke_style?: string;
    stroke_colour: string;
    fill_colour?: string;
    geom: {
        type: string;
        coordinates: [];
    };
    geom_properties?: {
        radius?: number;
        centre?: [number, number];
        height?: number;
    };
}

export const savedDrawnFeaturesAsync = (projectId: number, params: SavedDrawnFeatures): any => {
    return (dispatch: any): CancelTokenSource => {
        dispatch(saveDrawnFeature());
        const saveShapeSource = axios.CancelToken.source();

        const payload: PayloadStructure = {
            name: params.properties.name,
            stroke_style: params.properties.strokeStyle,
            stroke_colour: params.properties.strokeColour,
            fill_colour: params.properties.fillColour,
            geom: params.geometry
        };

        if (params.properties.radius) {
            payload.geom_properties = {
                radius: params.properties.radius,
                centre: params.properties.centre
            };
        }

        if (params.properties.height) {
            payload.geom_properties = {
                ...(payload.geom_properties ?? {}),
                height: params.properties.height
            };
        }

        axios
            .post(`/api/projects/${projectId}/drawn_features`, payload, {
                cancelToken: saveShapeSource.token
            })
            .then(response => {
                const savedFeaturesData: { geom: any; id: any } = response.data;
                dispatch(setSavedDrawnFeatures([savedFeaturesData.geom as never], MAIN_MAP));
                dispatch(setShapeFeatureProperty(params.id, 'id', savedFeaturesData.id, MAIN_MAP));
                dispatch(saveDrawnFeatureSuccess(savedFeaturesData));
                dispatch(
                    openSnackbar('success', `Successfully saved '${params.properties.name.trim()}'`)
                );
            })
            .catch(error => {
                console.error(error);
                dispatch(saveDrawnFeatureFailure(error.response));
                dispatch(openSnackbar('error', 'Shape has not been saved'));
            });

        return saveShapeSource;
    };
};

export const updateDrawnFeaturesAsync = (
    drawnFeatureId: number,
    params: SavedDrawnFeatures
): any => {
    return (dispatch: any): CancelTokenSource => {
        dispatch(updateDrawnFeature());
        const updateDrawingSource = axios.CancelToken.source();

        const payload: PayloadStructure = {
            name: params.properties.name,
            stroke_style: params.properties.strokeStyle,
            stroke_colour: params.properties.strokeColour,
            fill_colour: params.properties.fillColour,
            geom: params.geometry
        };

        if (params.properties.radius) {
            payload.geom_properties = {
                radius: params.properties.radius,
                centre: params.properties.centre
            };
        }

        if (params.properties.height) {
            payload.geom_properties = {
                ...(payload.geom_properties ?? {}),
                height: params.properties.height
            };
        }

        axios
            .patch(`/api/drawn_features/${drawnFeatureId}`, payload, {
                cancelToken: updateDrawingSource.token
            })
            .then(response => {
                const savedFeaturesData = response.data;
                dispatch(setSavedDrawnFeatures([savedFeaturesData.geom as never], MAIN_MAP));
                dispatch(updateDrawnFeatureSuccess(savedFeaturesData));
                dispatch(
                    openSnackbar(
                        'success',
                        `Successfully updated '${params.properties.name.trim()}'`
                    )
                );
            })
            .catch(error => {
                console.error(error);
                dispatch(updateDrawnFeatureFailure(error.response));
                dispatch(openSnackbar('error', 'Shape has not been updated'));
            });

        return updateDrawingSource;
    };
};

export const fetchAllDrawingsAsync = (projectId: number): any => {
    return async (dispatch: Dispatch): Promise<any> => {
        dispatch(fetchDrawnFeature());

        const fetchAllDrawingSource = axios.CancelToken.source();

        try {
            const response: AxiosResponse<any> = await axios.get(
                `/api/projects/${projectId}/drawn_features`,
                {
                    cancelToken: fetchAllDrawingSource.token
                }
            );

            const { data } = response;
            // Once drawings are fetched, it is passed as an array into
            // savedDrawnFeaturesArray
            if (data.length) {
                const newArray = data.map((item: { geom: any }) => item.geom);
                dispatch(setSavedDrawnFeatures(newArray, MAIN_MAP));
            } else {
                dispatch(setClearSavedDrawnFeatures([], MAIN_MAP));
            }

            dispatch(fetchDrawnFeatureSuccess(data));
            return data;
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request cancelled:', error.message);
            } else {
                console.error('Error fetching drawings:', error);
                dispatch(fetchDrawnFeatureFailure(error.response));
            }

            throw error;
        }
    };
};

export const deleteDrawnFeatureAsync = (id: number, name: string, savedFeatures: any[]): any => {
    return async (
        dispatch: Dispatch,
        getState: () => { (): any; new (): any; [x: string]: any }
    ): Promise<void> => {
        const state = getState()[MAIN_MAP];
        const drawModule = state.drawModule;
        const drawnFeatures = drawModule.getDrawControl().getAll();
        const drawingFeatures = drawModule.getDrawControl().getSelected();

        // Filters out the drawing that user wishes to remove
        const drawnFeaturesFiltered = drawnFeatures.features.filter(
            (feat: any) => feat.properties.id !== id
        );
        const drawingFeaturesFiltered = drawingFeatures.features.filter(
            (feat: any) => feat.properties.id !== id
        );
        // This is needed to get the MapBox generated id which is
        // later required to delete the shape
        const difference = drawnFeatures.features.filter(
            (x: any) => !drawnFeaturesFiltered.includes(x)
        );

        dispatch(deleteDrawnFeatureFromDB());

        try {
            const response = await axios.delete(`/api/drawn_features/${id}`, {
                cancelToken: axios.CancelToken.source().token
            });
            const filteredFeatures: any = savedFeatures.filter(
                feature => feature.properties.id !== id
            );
            if (Array.isArray(difference) && difference.length > 0) {
                // Delete the drawing from map if its already shown
                drawModule.getDrawControl().delete(difference[0].id);
            }
            // Clears the savedDrawnFeatures array and pushes the array
            // without including the deleted one
            dispatch(setClearSavedDrawnFeatures(true, MAIN_MAP));
            dispatch(setSavedDrawnFeatures(filteredFeatures, MAIN_MAP));

            dispatch(
                setDrawnFeatures(
                    {
                        type: 'FeatureCollection',
                        features: drawnFeaturesFiltered
                    },
                    MAIN_MAP
                )
            );
            dispatch(
                setDrawingFeature(
                    drawingFeaturesFiltered > 0 ? drawingFeaturesFiltered[0] : null,
                    MAIN_MAP
                )
            );
            dispatch(openSnackbar('success', `Successfully deleted '${name.trim()}'`));
            dispatch(deleteDrawnFeatureSuccess(response));
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error('Error deleting drawn feature:', error);

                dispatch(openSnackbar('error', 'Failed to delete the shape'));
                dispatch(deleteDrawnFeatureFailure(error.response));
            }
        }
    };
};
